<script setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import Button from 'dashboard/components-next/button/Button.vue';
import PrioritySelector from '../components/PrioritySelector.vue';
import AssigneeSelector from '../components/AssigneeSelector.vue';
import TeamSelector from '../components/TeamSelector.vue';
import ContactSelector from '../components/ContactSelector.vue';
import SlaPolicySelector from '../components/SlaPolicySelector.vue';
import BoardSelector from '../components/BoardSelector.vue';
import BoardColumnSelector from '../components/BoardColumnSelector.vue';
import ServiceSelector from '../components/ServiceSelector.vue';
import SubServiceSelector from '../components/SubServiceSelector.vue';
import CustomAttributeListSelector from '../components/CustomAttributeListSelector.vue';
import BusinessAccountSelector from '../components/BusinessAccountSelector.vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  contactId: {
    type: [Number, String],
    default: null,
  },
  conversationId: {
    type: [Number, String],
    default: null,
  },
  fetchContacts: {
    type: Boolean,
    default: true,
  },
  boardId: {
    type: [Number, String],
    default: null,
  },
  boardColumnId: {
    type: [Number, String],
    default: null,
  },
  emitOnCreate: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['ticketCreated', 'cancel']);

const router = useRouter();
const store = useStore();
const { t } = useI18n();

const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getMyTeams');
const allTeams = computed(() => store.getters['teams/getTeams'] || []);
const priorities = useMapGetter('tickets/getPriorities');
const contacts = useMapGetter('contacts/getContacts');
const slaPolicies = useMapGetter('tickets/getSlaPolicies');
const services = useMapGetter('services/getServices');
const boards = computed(() => store.getters['boards/getBoards'] || []);
const businessAccounts = computed(
  () => store.getters['businessAccounts/getBusinessAccountsList'] || []
);
const ticketAttributes = computed(
  () => store.getters['attributes/getTicketAttributes'] || []
);

const form = ref({
  title: '',
  description: '',
  priority: null, // Will be set to 'Média' priority ID
  contact: props.contactId ? Number(props.contactId) : null,
  assignedTo: null,
  team: null,
  slaPolicy: null,
  board: props.boardId ? Number(props.boardId) : null,
  boardColumn: props.boardColumnId ? Number(props.boardColumnId) : null,
  service: null,
  subService: null,
  businessAccount: null,
  customAttributes: {},
});

const isColumnCreateFlow = computed(
  () => !!(props.boardId && props.boardColumnId)
);

const boardTeamId = computed(() => {
  if (!props.boardId) return null;
  const selectedBoard = (boards.value || []).find(
    b => b.id === Number(props.boardId)
  );
  return selectedBoard?.team_id || null;
});

const servicesForSelector = computed(() => {
  const all = services.value || [];
  if (!isColumnCreateFlow.value) return all;

  const teamId = boardTeamId.value || form.value.team;
  if (!teamId) return all;

  return all.filter(service => service.team_id === teamId);
});

const errors = ref({});
const isSubmitting = ref(false);

const fetchPriorities = async () => {
  priorities.value = await store.dispatch('tickets/getPriorities');
};

const fetchContacts = async () => {
  if (props.fetchContacts && !props.contactId) {
    await store.dispatch('contacts/get');
  }
};

const fetchSlaPolicies = async () => {
  await store.dispatch('tickets/getSlaPolicies');
};

const fetchServices = async () => {
  await store.dispatch('services/get');
};

const fetchBoards = async () => {
  await store.dispatch('boards/getAll');
};

const fetchTicketAttributes = async () => {
  await store.dispatch('attributes/get');
};

const fetchBusinessAccounts = async () => {
  await store.dispatch('businessAccounts/get');
};

const boardColumns = computed(() => {
  if (!form.value.board) return [];
  const board = boards.value.find(b => b.id === form.value.board);
  return board?.board_columns || [];
});

const hasServices = computed(
  () => (servicesForSelector.value || []).length > 0
);

const isSameId = (firstId, secondId) =>
  firstId !== null &&
  firstId !== undefined &&
  secondId !== null &&
  secondId !== undefined &&
  String(firstId) === String(secondId);

const teamHasBoard = team =>
  (boards.value || []).some(b => b.team_id === team.id);

// When team is set by service and not in getMyTeams, use all teams so selector can display the name.
// Only show teams that have a board (or the currently selected team) to avoid tickets without board/column.
const teamsForSelector = computed(() => {
  const myTeams = teams.value || [];
  const teamId = form.value.team;
  const baseList =
    hasServices.value && teamId && !myTeams.some(t => t.id === teamId)
      ? allTeams.value
      : myTeams;
  return baseList.filter(
    t => teamHasBoard(t) || (teamId != null && t.id === teamId)
  );
});

const selectedService = computed(() =>
  (services.value || []).find(svc => isSameId(svc.id, form.value.service))
);

const subServices = computed(() => selectedService.value?.sub_services || []);

const syncBoardForTeam = async teamId => {
  if (!teamId) {
    form.value.board = null;
    form.value.boardColumn = null;
    return;
  }

  try {
    await store.dispatch('boards/getByTeam', teamId);
    const teamBoard = store.getters['boards/getBoard'];

    if (teamBoard) {
      const currentBoards = boards.value || [];
      const boardExists = currentBoards.some(b => b.id === teamBoard.id);
      if (!boardExists) {
        store.commit('boards/SET_BOARDS', [...currentBoards, teamBoard]);
      }

      form.value.board = teamBoard.id;

      await nextTick();
      if (
        !props.boardColumnId &&
        teamBoard.board_columns &&
        teamBoard.board_columns.length > 0
      ) {
        const sortedColumns = [...teamBoard.board_columns].sort(
          (a, b) => (a.position || 0) - (b.position || 0)
        );
        if (sortedColumns.length > 0) {
          form.value.boardColumn = sortedColumns[0].id;
        }
      } else if (props.boardColumnId) {
        form.value.boardColumn = Number(props.boardColumnId);
      }
    } else {
      form.value.board = null;
      form.value.boardColumn = null;
    }
  } catch (error) {
    // console.error('Error fetching board for team:', error);
    form.value.board = null;
    form.value.boardColumn = null;
  }
};

// Watch for team changes to auto-select board
watch(
  () => form.value.team,
  async newTeamId => {
    if (hasServices.value && !form.value.service) {
      form.value.board = null;
      form.value.boardColumn = null;
      return;
    }
    // Don't override board if boardId prop was provided
    if (props.boardId && form.value.board === Number(props.boardId)) {
      return;
    }

    await syncBoardForTeam(newTeamId);
  }
);

// Watch for board changes to auto-select first column
watch(
  () => form.value.board,
  async newBoardId => {
    if (newBoardId) {
      await nextTick();
      // If boardColumnId prop is provided, use it; otherwise select first column
      if (props.boardColumnId) {
        form.value.boardColumn = Number(props.boardColumnId);
      } else if (boardColumns.value.length > 0) {
        const sortedColumns = [...boardColumns.value].sort(
          (a, b) => (a.position || 0) - (b.position || 0)
        );
        if (sortedColumns.length > 0) {
          form.value.boardColumn = sortedColumns[0].id;
        }
      } else {
        form.value.boardColumn = null;
      }
    } else if (!props.boardColumnId) {
      form.value.boardColumn = null;
    }
  },
  { immediate: false }
);

watch(
  () => form.value.service,
  async newServiceId => {
    const service = (services.value || []).find(s =>
      isSameId(s.id, newServiceId)
    );
    if (!service) {
      form.value.team = null;
      form.value.slaPolicy = null;
      form.value.board = null;
      form.value.boardColumn = null;
      form.value.subService = null;
      return;
    }

    form.value.team = service.team_id || null;
    form.value.slaPolicy = service.sla_policy_id || null;
    form.value.subService = null;
    await syncBoardForTeam(service.team_id);
  }
);

watch(
  () => form.value.subService,
  newSubServiceId => {
    if (!newSubServiceId) {
      form.value.slaPolicy = selectedService.value?.sla_policy_id || null;
      return;
    }

    const sub = subServices.value.find(s => isSameId(s.id, newSubServiceId));
    form.value.slaPolicy = sub?.sla_policy_id || null;
  }
);

watch(
  priorities,
  newPriorities => {
    if (newPriorities && newPriorities.length > 0) {
      const mediaPriority = newPriorities.find(p => p.name === 'Média');
      if (mediaPriority) {
        form.value.priority = mediaPriority.id;
      }
    }
  },
  { immediate: true }
);

const validateForm = () => {
  errors.value = {};

  if (!form.value.title.trim()) {
    errors.value.title = t('TICKETS.TITLE_REQUIRED');
  }

  if (!form.value.description.trim()) {
    errors.value.description = t('TICKETS.DESCRIPTION_REQUIRED');
  }

  if (hasServices.value && !form.value.service) {
    errors.value.service = t('TICKETS.SERVICE.REQUIRED');
  }

  if (!form.value.team) {
    errors.value.team = t('TICKETS.TEAM_REQUIRED');
  }

  if (!form.value.slaPolicy) {
    errors.value.slaPolicy = t('TICKETS.SLA_REQUIRED');
  }

  if (!form.value.board) {
    errors.value.board = t('TICKETS.BOARD_REQUIRED');
  }

  if (!form.value.boardColumn) {
    errors.value.boardColumn = t('TICKETS.COLUMN_REQUIRED');
  }

  return Object.keys(errors.value).length === 0;
};

const handleSubmit = async () => {
  if (!validateForm()) {
    return;
  }

  isSubmitting.value = true;

  try {
    if (form.value.assignedTo && agents.value && agents.value.length > 0) {
      const assignee = agents.value.find(
        user => user.id === form.value.assignedTo
      );
      if (!assignee) {
        throw new Error(t('TICKETS.REGISTER.ERRORS.ASSIGNEE_NOT_FOUND'));
      }
    }

    // Validate team: must exist in my teams, unless it was set by the selected service
    const teamFromService =
      hasServices.value &&
      form.value.service &&
      selectedService.value?.team_id === form.value.team;
    if (form.value.team && !teamFromService) {
      const teamList = teams.value || [];
      if (teamList.length > 0) {
        const team = teamList.find(
          team_item => team_item.id === form.value.team
        );
        if (!team) {
          throw new Error(t('TICKETS.REGISTER.ERRORS.TEAM_NOT_FOUND'));
        }
      }
    }

    if (form.value.contact && contacts.value && contacts.value.length > 0) {
      const contact = contacts.value.find(c => c.id === form.value.contact);
      if (!contact) {
        throw new Error(t('TICKETS.REGISTER.ERRORS.CONTACT_NOT_FOUND'));
      }
    }

    if (!priorities.value || priorities.value.length === 0) {
      throw new Error(t('TICKETS.REGISTER.ERRORS.PRIORITIES_NOT_LOADED'));
    }
    const priorityObj = priorities.value.find(
      p => p.id === form.value.priority
    );
    if (!priorityObj) {
      throw new Error(t('TICKETS.REGISTER.ERRORS.INVALID_PRIORITY'));
    }

    const customAttributes = {};
    Object.keys(form.value.customAttributes).forEach(key => {
      const value = form.value.customAttributes[key];
      const attribute = ticketAttributes.value.find(
        attr => attr.attributeKey === key
      );

      if (attribute?.attributeDisplayType === 'checkbox') {
        if (value !== null && value !== undefined) {
          customAttributes[key] = Boolean(value);
        }
      } else if (value !== null && value !== undefined && value !== '') {
        if (
          attribute?.attributeDisplayType === 'date' &&
          typeof value === 'string'
        ) {
          const date = new Date(value);
          if (!Number.isNaN(date.getTime())) {
            customAttributes[key] = date.toISOString();
          }
        } else {
          customAttributes[key] = value;
        }
      }
    });

    const ticketData = {
      title: form.value.title,
      description: form.value.description,
      priority_id: priorityObj.id,
      assignee_id: form.value.assignedTo,
      team_id: form.value.team,
      contact_id: form.value.contact,
      conversation_id: props.conversationId,
      sla_policy_id: form.value.slaPolicy,
      board_id: form.value.board,
      board_column_id: form.value.boardColumn,
      service_id: hasServices.value ? form.value.service : undefined,
      sub_service_id: hasServices.value ? form.value.subService : undefined,
      business_account_id: form.value.businessAccount || undefined,
      custom_attributes:
        Object.keys(customAttributes).length > 0 ? customAttributes : undefined,
    };

    await store.dispatch('tickets/create', ticketData);

    useAlert(t('TICKETS.REGISTER.SUCCESS.TICKET_CREATED'));

    if (props.conversationId || props.emitOnCreate) {
      emit('ticketCreated');
    } else {
      router.push({ name: 'helpdesk_index' });
    }
  } catch (error) {
    if (error.response?.data?.errors) {
      const errorMessages = error.response.data.errors.join('\n');
      useAlert(
        `${t('TICKETS.REGISTER.ERRORS.VALIDATION_ERROR')}${errorMessages}`
      );
    } else if (error.response?.status === 404) {
      useAlert(t('TICKETS.REGISTER.ERRORS.USER_OR_TEAM_NOT_FOUND'));
    } else if (error.response?.status === 422) {
      useAlert(t('TICKETS.REGISTER.ERRORS.INVALID_DATA'));
    } else {
      useAlert(
        `${t('TICKETS.REGISTER.ERRORS.CREATE_ERROR')}${error.message || t('TICKETS.REGISTER.ERRORS.TRY_AGAIN')}`
      );
    }
  } finally {
    isSubmitting.value = false;
  }
};

const onCancel = () => {
  if (props.conversationId || props.emitOnCreate) {
    emit('cancel');
  } else {
    router.push({ name: 'helpdesk_index' });
  }
};

const ensureContactInStore = async () => {
  if (props.contactId) {
    const existingContact = store.getters['contacts/getContact'](
      props.contactId
    );
    if (!existingContact || !existingContact.id) {
      await store.dispatch('contacts/show', { id: props.contactId });
    }
  }
};

onMounted(async () => {
  fetchPriorities();
  fetchContacts();
  ensureContactInStore();
  fetchSlaPolicies();
  fetchServices();
  await fetchBoards();
  fetchTicketAttributes();
  fetchBusinessAccounts();

  // If boardId is provided (e.g. Kanban column create), set up the board and team
  if (props.boardId) {
    await nextTick();
    const boardIdNum = Number(props.boardId);
    let selectedBoard = (boards.value || []).find(b => b.id === boardIdNum);
    // Fallback: use current board from store when opening from Kanban (getBoard may be set before getAll list)
    if (!selectedBoard) {
      const currentBoard = store.getters['boards/getBoard'];
      if (currentBoard?.id === boardIdNum) selectedBoard = currentBoard;
    }
    if (selectedBoard) {
      form.value.board = selectedBoard.id;
      if (selectedBoard.team_id) {
        form.value.team = selectedBoard.team_id;
      }
      if (props.boardColumnId) {
        form.value.boardColumn = Number(props.boardColumnId);
      } else if (
        selectedBoard.board_columns &&
        selectedBoard.board_columns.length > 0
      ) {
        const sortedColumns = [...selectedBoard.board_columns].sort(
          (a, b) => (a.position || 0) - (b.position || 0)
        );
        if (sortedColumns.length > 0) {
          form.value.boardColumn = sortedColumns[0].id;
        }
      }
    }
  }
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <div v-if="!conversationId" class="border-b border-n-weak p-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold text-n-slate-12">
            {{ $t('TICKETS.REGISTER.TITLE') }}
          </h1>
          <p class="text-n-slate-11 mt-1">
            {{ $t('TICKETS.REGISTER.DESCRIPTION') }}
          </p>
        </div>
        <Button
          v-if="!emitOnCreate"
          variant="ghost"
          class="text-n-slate-11 hover:text-n-slate-12"
          @click="router.push({ name: 'helpdesk_index' })"
        >
          {{ $t('TICKETS.REGISTER.BACK_TO_HELPDESK') }}
        </Button>
      </div>
    </div>

    <div class="flex-1 py-6 px-1 overflow-y-auto mx-auto max-w-2xl w-full">
      <form class="" @submit.prevent="handleSubmit">
        <div class="mb-6">
          <label
            for="title"
            class="block text-sm font-medium text-n-slate-12 mb-2"
          >
            {{ $t('TICKETS.REGISTER.FORM.TITLE') }}
          </label>
          <input
            id="title"
            v-model="form.title"
            type="text"
            class="w-full px-3 py-2 border border-n-weak rounded-lg bg-n-solid-3 text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            :placeholder="$t('TICKETS.REGISTER.FORM.TITLE_PLACEHOLDER')"
            :class="{ 'border-red-500': errors.title }"
            :disabled="isSubmitting"
          />
          <p v-if="errors.title" class="text-red-500 text-sm mt-1">
            {{ errors.title }}
          </p>
        </div>

        <!-- Description -->
        <div class="mb-6">
          <label
            for="description"
            class="block text-sm font-medium text-n-slate-12 mb-2"
          >
            {{ $t('TICKETS.REGISTER.FORM.DESCRIPTION') }}
          </label>
          <textarea
            id="description"
            v-model="form.description"
            rows="4"
            class="w-full px-3 py-2 border border-n-weak rounded-lg bg-n-solid-3 text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            :placeholder="$t('TICKETS.REGISTER.FORM.DESCRIPTION_PLACEHOLDER')"
            :class="{ 'border-red-500': errors.description }"
            :disabled="isSubmitting"
          />
          <p v-if="errors.description" class="text-red-500 text-sm mt-1">
            {{ errors.description }}
          </p>
        </div>

        <!-- Priority -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ $t('TICKETS.REGISTER.FORM.PRIORITY') }}
          </label>
          <PrioritySelector
            :priorities="priorities || []"
            :selected-priority-id="form.priority"
            :update-history="false"
            @update:selected-priority-id="form.priority = $event"
          />
        </div>

        <div v-if="!contactId" class="mb-6">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ $t('TICKETS.REGISTER.FORM.CONTACT') }}
          </label>
          <ContactSelector
            :contacts="contacts || []"
            :selected-contact-id="form.contact"
            :update-history="false"
            @update:selected-contact-id="form.contact = $event"
          />
        </div>

        <!-- Business Account -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ t('TICKETS.BUSINESS_ACCOUNT') }}
          </label>
          <BusinessAccountSelector
            :business-accounts="businessAccounts || []"
            :selected-business-account-id="form.businessAccount"
            :update-history="false"
            :disabled="isSubmitting"
            @update:selected-business-account-id="form.businessAccount = $event"
          />
        </div>

        <!-- Assigned To -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ $t('TICKETS.REGISTER.FORM.ASSIGNEE') }}
          </label>
          <AssigneeSelector
            :agents="agents || []"
            :selected-assignee-id="form.assignedTo"
            :update-history="false"
            @update:selected-assignee-id="form.assignedTo = $event"
          />
        </div>

        <!-- Service -->
        <div v-if="hasServices" class="mb-6">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ t('TICKETS.REGISTER.FORM.SERVICE') }}
          </label>
          <ServiceSelector
            :services="servicesForSelector || []"
            :selected-service-id="form.service"
            :disabled="isSubmitting"
            @update:selected-service-id="form.service = $event"
          />
          <p v-if="errors.service" class="text-red-500 text-sm mt-1">
            {{ errors.service }}
          </p>
        </div>

        <!-- Subservice -->
        <div
          v-if="hasServices && subServices.length > 0 && form.service"
          class="mb-6"
        >
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ t('TICKETS.SUB_SERVICE.LABEL') }}
          </label>
          <SubServiceSelector
            :sub-services="subServices"
            :selected-sub-service-id="form.subService"
            :disabled="isSubmitting"
            @update:selected-sub-service-id="form.subService = $event"
          />
        </div>

        <!-- Team -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ $t('TICKETS.REGISTER.FORM.TEAM') }}
          </label>
          <TeamSelector
            :teams="teamsForSelector || []"
            :selected-team-id="form.team"
            :update-history="false"
            :disabled="isSubmitting || hasServices"
            @update:selected-team-id="form.team = $event"
          />
          <p v-if="errors.team" class="text-red-500 text-sm mt-1">
            {{ errors.team }}
          </p>
        </div>

        <!-- SLA Policy -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ $t('TICKETS.REGISTER.FORM.SLA_POLICY') }}
          </label>
          <SlaPolicySelector
            :sla-policies="slaPolicies || []"
            :selected-sla-policy-id="form.slaPolicy"
            :ticket-id="0"
            :update-history="false"
            :disabled="isSubmitting || hasServices"
            :show-time-limits="false"
            @update:sla-policy-id="form.slaPolicy = $event"
          />
          <p v-if="errors.slaPolicy" class="text-red-500 text-sm mt-1">
            {{ errors.slaPolicy }}
          </p>
        </div>

        <!-- Board -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ $t('TICKETS.REGISTER.FORM.BOARD') }}
          </label>
          <BoardSelector
            :boards="boards || []"
            :selected-board-id="form.board"
            :update-history="false"
            disabled
            @update:selected-board-id="form.board = $event"
          />
          <p v-if="errors.board" class="text-red-500 text-sm mt-1">
            {{ errors.board }}
          </p>
          <p
            v-if="form.team && !form.board"
            class="text-n-slate-10 text-sm mt-1"
          >
            {{ $t('TICKETS.REGISTER.FORM.BOARD_AUTO_FILL') }}
          </p>
        </div>

        <!-- Board Column -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ $t('TICKETS.REGISTER.FORM.COLUMN') }}
          </label>
          <BoardColumnSelector
            :columns="boardColumns"
            :selected-column-id="form.boardColumn"
            :board-id="form.board"
            :update-history="false"
            disabled
            @update:selected-column-id="form.boardColumn = $event"
          />
          <p v-if="errors.boardColumn" class="text-red-500 text-sm mt-1">
            {{ errors.boardColumn }}
          </p>
          <p
            v-if="form.board && !form.boardColumn"
            class="text-n-slate-10 text-sm mt-1"
          >
            {{ $t('TICKETS.REGISTER.FORM.COLUMN_AUTO_FILL') }}
          </p>
        </div>

        <!-- Custom Attributes -->
        <div v-if="ticketAttributes.length > 0" class="mb-6">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ $t('TICKETS.REGISTER.FORM.CUSTOM_ATTRIBUTES') }}
          </label>
          <div class="space-y-4">
            <div
              v-for="attribute in ticketAttributes"
              :key="attribute.id"
              class="flex flex-col gap-1"
            >
              <label
                :for="`custom-attr-${attribute.id}`"
                class="text-sm font-medium text-n-slate-11"
              >
                {{ attribute.attributeDisplayName }}
              </label>

              <!-- Checkbox Type -->
              <div
                v-if="attribute.attributeDisplayType === 'checkbox'"
                class="flex items-center gap-2 py-1.5"
              >
                <input
                  :id="`custom-attr-${attribute.id}`"
                  type="checkbox"
                  :checked="
                    form.customAttributes[attribute.attributeKey] || false
                  "
                  class="w-4 h-4 text-blue-600 border-n-weak rounded focus:ring-2 focus:ring-blue-500"
                  :disabled="isSubmitting"
                  @change="
                    form.customAttributes[attribute.attributeKey] =
                      $event.target.checked
                  "
                />
                <span class="text-sm text-n-slate-11">
                  {{
                    form.customAttributes[attribute.attributeKey]
                      ? $t('TICKETS.REGISTER.FORM.YES')
                      : $t('TICKETS.REGISTER.FORM.NO')
                  }}
                </span>
              </div>

              <!-- List Type -->
              <CustomAttributeListSelector
                v-else-if="attribute.attributeDisplayType === 'list'"
                :attribute="attribute"
                :selected-value="form.customAttributes[attribute.attributeKey]"
                :disabled="isSubmitting"
                @update:selected-value="
                  form.customAttributes[attribute.attributeKey] = $event
                "
              />

              <!-- Number Type -->
              <input
                v-else-if="attribute.attributeDisplayType === 'number'"
                :id="`custom-attr-${attribute.id}`"
                v-model.number="form.customAttributes[attribute.attributeKey]"
                type="number"
                step="any"
                class="w-full px-3 py-2 border border-n-weak rounded-lg bg-n-solid-3 text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                :placeholder="`Digite o valor para ${attribute.attributeDisplayName}`"
                :disabled="isSubmitting"
              />

              <!-- Date Type -->
              <input
                v-else-if="attribute.attributeDisplayType === 'date'"
                :id="`custom-attr-${attribute.id}`"
                v-model="form.customAttributes[attribute.attributeKey]"
                type="date"
                class="w-full px-3 py-2 border border-n-weak rounded-lg bg-n-solid-3 text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                :disabled="isSubmitting"
              />

              <!-- Text Type (default) -->
              <input
                v-else
                :id="`custom-attr-${attribute.id}`"
                v-model="form.customAttributes[attribute.attributeKey]"
                type="text"
                class="w-full px-3 py-2 border border-n-weak rounded-lg bg-n-solid-3 text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                :placeholder="`Digite o valor para ${attribute.attributeDisplayName}`"
                :disabled="isSubmitting"
              />
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-3">
          <Button
            type="button"
            color="blue"
            size="lg"
            :is-loading="isSubmitting"
            class="px-8"
            @click="handleSubmit"
          >
            {{
              isSubmitting
                ? $t('TICKETS.REGISTER.FORM.CREATING')
                : $t('TICKETS.REGISTER.FORM.CREATE_TICKET')
            }}
          </Button>

          <Button
            type="button"
            variant="outline"
            color="slate"
            size="lg"
            :disabled="isSubmitting"
            class="px-8"
            @click="onCancel"
          >
            {{ $t('TICKETS.REGISTER.FORM.CANCEL') }}
          </Button>
        </div>
      </form>
    </div>
  </div>
</template>
